import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { CheckIcon } from "@heroicons/react/20/solid";
import { applyToJobOpening } from "../../api/jobOpenings";
import {
  CV_ACCEPT,
  CV_MAX_BYTES,
  firstApiError,
  formatCvSize,
  isAcceptedCvFile,
  publicJobOpeningContact,
  publicJobOtherContacts,
  requirementItemsFromJob,
  splitFullName,
} from "../../data/jobOpeningPublic";
import { JobContactSections } from "./JobContactSections";

const schema = Yup.object({
  full_name: Yup.string()
    .trim()
    .required("Skriv navnet ditt")
    .test(
      "full-name",
      "Skriv fornavn og etternavn",
      (value) => splitFullName(value).last_name.length > 0
    ),
  email: Yup.string()
    .trim()
    .email("E-posten ser ikke riktig ut")
    .required("Skriv e-posten din"),
  phone: Yup.string().trim(),
  city: Yup.string().trim(),
  cover_letter: Yup.string().trim().max(4000, "Hold teksten under 4000 tegn"),
  linkedin_url: Yup.string()
    .trim()
    .test(
      "linkedin-url",
      "Lenken må starte med http:// eller https://",
      (value) => {
        if (!value) return true;
        try {
          const url = new URL(value);
          return url.protocol === "http:" || url.protocol === "https:";
        } catch {
          return false;
        }
      }
    ),
  available_from: Yup.string(),
  consent_privacy: Yup.boolean().oneOf(
    [true],
    "Du må godta at vi behandler opplysningene"
  ),
});

const initialValues = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  cover_letter: "",
  linkedin_url: "",
  available_from: "",
  consent_privacy: false,
};

export function JobApplicationForm({ job }) {
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  if (!job || job._static) {
    return <StaticApplyFallback job={job} />;
  }

  if (job.website_form_enabled !== true) {
    return <StaticApplyFallback job={job} reason="form_disabled" />;
  }

  if (submitted) {
    return (
      <div className="apply-success-card">
        <div className="apply-success-icon" aria-hidden="true">
          <CheckIcon />
        </div>
        <h3>Søknaden er sendt</h3>
        <p>
          Takk{submittedName ? `, ${submittedName}` : ""}. Vi har mottatt
          søknaden din til <strong>{job.title}</strong> og tar kontakt når vi
          har vurdert den.
        </p>
      </div>
    );
  }

  return (
    <ApplyForm
      job={job}
      onSuccess={(name) => {
        setSubmittedName(name);
        setSubmitted(true);
      }}
    />
  );
}

function ApplyForm({ job, onSuccess }) {
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [checkedRequirements, setCheckedRequirements] = useState({});
  const requirementItems = requirementItemsFromJob(job);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        if (cvError) {
          helpers.setSubmitting(false);
          return;
        }
        const names = splitFullName(values.full_name);
        try {
          const res = await applyToJobOpening(
            job.slug || job.id,
            {
              first_name: names.first_name,
              last_name: names.last_name,
              email: values.email.trim(),
              phone: values.phone.trim(),
              city: values.city.trim(),
              cover_letter: values.cover_letter.trim(),
              linkedin_url: values.linkedin_url.trim(),
              available_from: values.available_from,
              consent_privacy: values.consent_privacy,
              requirement_checks: requirementItems.map((item) => ({
                id: item.id,
                checked: !!checkedRequirements[item.id],
              })),
            },
            cvFile
          );
          if (res?.data?.id || res?.id) {
            toast.success("Søknaden er sendt. Takk!");
            onSuccess(names.first_name);
            return;
          }
          const message = firstApiError(res) || "Kunne ikke sende søknaden.";
          helpers.setStatus({ error: message });
          toast.error(message);
        } catch {
          helpers.setStatus({ error: "Kunne ikke sende søknaden." });
          toast.error("Kunne ikke sende søknaden.");
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form className="apply-form" noValidate>
          <div className="apply-fields">
            <ApplyField
              label="Navn"
              name="full_name"
              autoComplete="name"
              placeholder="Fornavn Etternavn"
              required
              formik={formik}
            />
            <div className="apply-row">
              <ApplyField
                label="E-post"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="deg@epost.no"
                required
                formik={formik}
              />
              <ApplyField
                label="Telefon"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="Valgfritt, men anbefalt"
                formik={formik}
              />
            </div>

            {requirementItems.length > 0 ? (
              <fieldset className="apply-requirements">
                <legend className="apply-label">Kvalifikasjoner</legend>
                <p className="apply-hint">
                  Kryss av det som gjelder for deg.
                </p>
                <div className="apply-requirement-list">
                  {requirementItems.map((item) => (
                    <label key={item.id} className="stilling-consent">
                      <input
                        type="checkbox"
                        checked={!!checkedRequirements[item.id]}
                        onChange={(e) =>
                          setCheckedRequirements((prev) => ({
                            ...prev,
                            [item.id]: e.target.checked,
                          }))
                        }
                      />
                      <span>{item.text}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <CvDropzone
              file={cvFile}
              error={cvError}
              onFile={(file, error) => {
                setCvFile(file);
                setCvError(error);
              }}
            />

            <button
              type="button"
              className="apply-more-toggle"
              aria-expanded={showMore}
              onClick={() => setShowMore((open) => !open)}
            >
              {showMore ? "Skjul ekstra felter" : "Legg til mer (valgfritt)"}
            </button>

            {showMore ? (
              <div className="apply-more">
                <div className="apply-row">
                  <ApplyField
                    label="Sted"
                    name="city"
                    autoComplete="address-level2"
                    placeholder="F.eks. Sarpsborg"
                    formik={formik}
                  />
                  <ApplyField
                    label="Tilgjengelig fra"
                    name="available_from"
                    type="date"
                    formik={formik}
                  />
                </div>
                <ApplyField
                  label="LinkedIn"
                  name="linkedin_url"
                  type="url"
                  placeholder="https://www.linkedin.com/in/…"
                  formik={formik}
                />
                <label className="apply-label">
                  <span className="apply-label-text">Kort søknadstekst</span>
                  <Field
                    as="textarea"
                    name="cover_letter"
                    rows={4}
                    className="apply-input apply-textarea"
                    placeholder="Hvorfor passer du? Hopp over hvis CV-en sier det."
                  />
                  <FieldError name="cover_letter" formik={formik} />
                </label>
              </div>
            ) : null}

            <label className="stilling-consent">
              <Field type="checkbox" name="consent_privacy" />
              <span>
                Jeg godtar at Airon behandler opplysningene mine i forbindelse
                med denne søknaden.
              </span>
            </label>
            <FieldError name="consent_privacy" formik={formik} />

            {formik.status?.error ? (
              <div className="apply-banner" role="alert">
                {formik.status.error}
              </div>
            ) : null}

            <button
              type="submit"
              className="btn apply-submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Sender…" : "Send søknad"}
              {!formik.isSubmitting ? <span className="arr">→</span> : null}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

function ApplyField({ label, name, formik, required, ...props }) {
  const invalid = formik.touched[name] && formik.errors[name];
  return (
    <label className="apply-label">
      <span className="apply-label-text">
        {label}
        {required ? (
          <span className="apply-required" aria-hidden="true">
            *
          </span>
        ) : null}
      </span>
      <Field
        name={name}
        className={`apply-input${invalid ? " is-invalid" : ""}`}
        aria-required={required ? "true" : undefined}
        required={required}
        {...props}
      />
      <FieldError name={name} formik={formik} />
    </label>
  );
}

function FieldError({ name, formik }) {
  if (!formik.touched[name] || !formik.errors[name]) return null;
  return <span className="apply-error">{formik.errors[name]}</span>;
}

function CvDropzone({ file, error, onFile }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const takeFile = (next) => {
    if (!next) {
      onFile(null, "");
      return;
    }
    if (!isAcceptedCvFile(next)) {
      onFile(null, `Filtypen støttes ikke. Bruk ${CV_ACCEPT}.`);
      return;
    }
    if (next.size > CV_MAX_BYTES) {
      onFile(null, `Filen er for stor. Maks ${formatCvSize(CV_MAX_BYTES)}.`);
      return;
    }
    onFile(next, "");
  };

  return (
    <div>
      <div className="apply-label">CV</div>
      <p className="apply-hint">PDF, Word eller bilde. Valgfritt, maks 8 MB.</p>
      <div
        className={`cv-drop${drag ? " is-drag" : ""}${file ? " has-file" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          takeFile(e.dataTransfer.files?.[0] || null);
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept={CV_ACCEPT}
          hidden
          onChange={(e) => takeFile(e.target.files?.[0] || null)}
        />
        {file ? (
          <div className="cv-file">
            <strong>{file.name}</strong>
            <span>{formatCvSize(file.size)}</span>
            <button
              type="button"
              className="cv-remove"
              onClick={(e) => {
                e.stopPropagation();
                takeFile(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
            >
              Fjern
            </button>
          </div>
        ) : (
          <div>
            <strong>Slipp CV-en her</strong>
            <span>eller klikk for å velge fil</span>
          </div>
        )}
      </div>
      {error ? <span className="apply-error">{error}</span> : null}
    </div>
  );
}

function StaticApplyFallback({ job, reason } = {}) {
  const formDisabled = reason === "form_disabled";
  const hasContact =
    !!publicJobOpeningContact(job) || publicJobOtherContacts(job).length > 0;
  return (
    <div className="apply-fallback">
      <h3>Søk via kontakt</h3>
      <p>
        {formDisabled
          ? "Søknadsskjemaet er ikke aktivert for denne stillingen. Send oss en melding, så tar vi søknaden videre."
          : "Denne stillingen vises som reserve. Send oss en melding, så tar vi søknaden videre."}
      </p>
      {hasContact ? (
        <div className="stilling-contact" style={{ margin: "16px 0" }}>
          <JobContactSections
            job={job}
            compact
            titleStyle={{ fontSize: 16, margin: "0 0 8px" }}
          />
        </div>
      ) : null}
      <Link to="/kontakt" className="btn">
        Kontakt oss <span className="arr">→</span>
      </Link>
    </div>
  );
}
